import React, { useEffect, useState } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  deleteDoc,
  setDoc,
  updateDoc 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../services/firebase';
import toast from 'react-hot-toast';

function decodeHtmlEntities(text) {
  if (!text) return '';
  return text
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ');
}

function formatSender(rawSender) {
  if (!rawSender) return '';
  return rawSender.replace(/^"/, '').replace(/"(\s*<)/, '$1');
}

/**
 * Determines whether a message is an Outbound Milestone Trigger or an Inbound Email
 */
function getMessageCategory(msg) {
  if (msg.triggerType && msg.triggerType.startsWith('chart_milestone')) {
    return 'outbound';
  }
  return 'inbound';
}

export default function AdminMessageQueue() {
  const [messages, setMessages] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View Toggle State: 'inbound' | 'outbound'
  const [activeTab, setActiveTab] = useState('inbound');

  // Track individual loading states per draft ID
  const [generatingIds, setGeneratingIds] = useState({});
  
  // Track local edits made by the admin per draft ID
  const [editableDrafts, setEditableDrafts] = useState({});

  useEffect(() => {
    const queueRef = collection(db, 'messageQueue');
    const q = query(
      queueRef,
      where('status', 'in', ['pending_review', 'pending_ai', 'pending', 'awaiting_review'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMessages(items);

      if (items.length > 0 && !selectedEmail) {
        setSelectedEmail(items[0].recipientEmail);
      }

      setLoading(false);
    }, (error) => {
      console.error('Error fetching messageQueue:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedEmail]);

  // Filter messages based on the selected activeTab ('inbound' vs 'outbound')
  const filteredMessages = messages.filter((msg) => getMessageCategory(msg) === activeTab);

  // Dynamic counts for tab badges
  const inboundCount = messages.filter((msg) => getMessageCategory(msg) === 'inbound').length;
  const outboundCount = messages.filter((msg) => getMessageCategory(msg) === 'outbound').length;

  // Group filtered messages by recipient email
  const groupedMessages = filteredMessages.reduce((groups, msg) => {
    const email = msg.recipientEmail || 'Unknown Sender';
    if (!groups[email]) {
      groups[email] = [];
    }
    groups[email].push(msg);
    return groups;
  }, {});

  const customerEmails = Object.keys(groupedMessages);
  const activeCustomerDrafts = selectedEmail ? (groupedMessages[selectedEmail] || []) : [];

  const handleFieldChange = (draftId, field, value) => {
    setEditableDrafts((prev) => ({
      ...prev,
      [draftId]: {
        ...prev[draftId],
        [field]: value
      }
    }));
  };

  const handleDelete = async (item) => {
    if (!item) return;
    try {
      if (item.messageId) {
        await setDoc(doc(db, 'processedMessages', item.messageId), {
          messageId: item.messageId,
          deletedAt: new Date().toISOString(),
          reason: 'user_deleted'
        });
      }

      await deleteDoc(doc(db, 'messageQueue', item.id));
      toast.success("Draft deleted.");
    } catch (err) {
      console.error('Failed to delete message:', err);
      toast.error("Failed to delete draft.");
    }
  };

  const handleSaveDraftEdits = async (draft) => {
    const currentEdit = editableDrafts[draft.id] || {};
    const finalSubject = currentEdit.suggestedSubject !== undefined ? currentEdit.suggestedSubject : (draft.suggestedSubject || draft.incomingSubject || "");
    const finalBody = currentEdit.suggestedBody !== undefined ? currentEdit.suggestedBody : (draft.suggestedBody || "");

    try {
      const docRef = doc(db, 'messageQueue', draft.id);
      await updateDoc(docRef, {
        suggestedSubject: finalSubject,
        suggestedBody: finalBody,
        status: 'pending_review',
        updatedAt: new Date().toISOString()
      });
      
      setEditableDrafts((prev) => {
        const nextState = { ...prev };
        delete nextState[draft.id];
        return nextState;
      });

      toast.success("Draft edits saved!");
    } catch (err) {
      console.error('Failed to save draft edits:', err);
      toast.error("Failed to save changes.");
    }
  };

  const handleRegenerate = async (item) => {
    if (!item) return;
    
    setGeneratingIds((prev) => ({ ...prev, [item.id]: true }));

    try {
      const docRef = doc(db, 'messageQueue', item.id);
      await updateDoc(docRef, {
        status: 'pending_ai',
        updatedAt: new Date().toISOString()
      });

      const functions = getFunctions();
      const generateAiDraftOnDemand = httpsCallable(functions, 'generateAiDraftOnDemand');
      await generateAiDraftOnDemand({ docId: item.id });

      setEditableDrafts((prev) => {
        const nextState = { ...prev };
        delete nextState[item.id];
        return nextState;
      });

      toast.success("AI draft generated!");

    } catch (err) {
      console.error('Failed to generate AI draft:', err);
      const docRef = doc(db, 'messageQueue', item.id);
      await updateDoc(docRef, { status: 'pending_review' }).catch(() => {});
      toast.error("Generation failed.");
    } finally {
      setGeneratingIds((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleApproveAndSend = async (draft) => {
    const currentEdit = editableDrafts[draft.id] || {};
    const finalSubject = currentEdit.suggestedSubject !== undefined ? currentEdit.suggestedSubject : (draft.suggestedSubject || draft.incomingSubject || "");
    const finalBody = currentEdit.suggestedBody !== undefined ? currentEdit.suggestedBody : (draft.suggestedBody || "");

    try {
      const docRef = doc(db, "messageQueue", draft.id);
      await updateDoc(docRef, {
        suggestedSubject: finalSubject,
        suggestedBody: finalBody,
        status: "approved",
        approvedAt: new Date().toISOString()
      });
      toast.success("Approved & queued for sending!");
    } catch (err) {
      console.error("Failed to approve draft:", err);
      toast.error("Approval failed.");
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500 font-medium">Loading TOM Message Queue...</div>;
  }

  return (
    <div className="flex h-[calc(100vh-160px)] border rounded-lg bg-white overflow-hidden shadow-sm">
      {/* LEFT SIDEBAR: Customer Groups & View Toggle */}
      <div className="w-1/3 border-r bg-gray-50 flex flex-col min-w-[280px]">
        
        {/* 🌟 INBOUND / OUTBOUND VIEW TOGGLE BAR */}
        <div className="p-2 border-b bg-gray-100 flex gap-1">
          <button
            onClick={() => {
              setActiveTab('inbound');
              const firstInbound = messages.find(m => getMessageCategory(m) === 'inbound');
              if (firstInbound) setSelectedEmail(firstInbound.recipientEmail);
            }}
            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-bold transition flex items-center justify-center gap-2 ${
              activeTab === 'inbound'
                ? 'bg-white text-blue-600 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>Inbound</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
              activeTab === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-600'
            }`}>
              {inboundCount}
            </span>
          </button>

          <button
            onClick={() => {
              setActiveTab('outbound');
              const firstOutbound = messages.find(m => getMessageCategory(m) === 'outbound');
              if (firstOutbound) setSelectedEmail(firstOutbound.recipientEmail);
            }}
            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-bold transition flex items-center justify-center gap-2 ${
              activeTab === 'outbound'
                ? 'bg-white text-purple-600 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>Outbound</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
              activeTab === 'outbound' ? 'bg-purple-100 text-purple-800' : 'bg-gray-200 text-gray-600'
            }`}>
              {outboundCount}
            </span>
          </button>
        </div>

        {/* Customer List */}
        <div className="divide-y overflow-y-auto flex-1">
          {customerEmails.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400">
              No {activeTab} messages pending.
            </div>
          ) : (
            customerEmails.map((email) => {
              const customerDrafts = groupedMessages[email];
              const isSelected = selectedEmail === email;
              const latestDraft = customerDrafts[0];

              return (
                <div
                  key={email}
                  onClick={() => setSelectedEmail(email)}
                  className={`p-3 cursor-pointer transition ${
                    isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-sm text-gray-900 truncate">
                      {formatSender(email)}
                    </span>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-800">
                      {customerDrafts.length} {customerDrafts.length === 1 ? 'Draft' : 'Drafts'}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-gray-700 truncate">
                    Latest: {decodeHtmlEntities(latestDraft.incomingSubject) || 'No Subject'}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT PANEL: Stacked Customer Drafts */}
      {selectedEmail && activeCustomerDrafts.length > 0 ? (
        <div className="w-2/3 flex flex-col h-full bg-white">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-base font-bold text-gray-900">
              {formatSender(selectedEmail)}
            </h3>
            <p className="text-xs text-gray-500">
              {activeCustomerDrafts.length} draft(s) awaiting approval for this recipient.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {activeCustomerDrafts.map((draft) => {
              const isGenerating = generatingIds[draft.id] || draft.status === 'pending_ai';
              
              const currentEdit = editableDrafts[draft.id] || {};
              const displaySubject = currentEdit.suggestedSubject !== undefined ? currentEdit.suggestedSubject : (draft.suggestedSubject || draft.incomingSubject || "");
              const displayBody = currentEdit.suggestedBody !== undefined ? currentEdit.suggestedBody : (draft.suggestedBody || "");

              const originalSubject = draft.suggestedSubject || draft.incomingSubject || "";
              const originalBody = draft.suggestedBody || "";
              
              const isSubjectEdited = currentEdit.suggestedSubject !== undefined && currentEdit.suggestedSubject !== originalSubject;
              const isBodyEdited = currentEdit.suggestedBody !== undefined && currentEdit.suggestedBody !== originalBody;
              const hasUnsavedEdits = isSubjectEdited || isBodyEdited;

              return (
                <div key={draft.id} className="border rounded-xl p-5 shadow-sm bg-white space-y-4">
                  <div className="flex justify-between items-center border-b pb-3 mb-3">
                    <div>
                      <h4 className="font-bold text-base text-gray-900">{draft.recipientEmail}</h4>
                      <div className="flex gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          draft.hasAccount 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-amber-100 text-amber-800 border border-amber-200'
                        }`}>
                          {draft.hasAccount ? '✓ Registered Account' : '✕ Unregistered / Legacy'}
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded border">
                          Type: {draft.triggerType || 'inbound_email'}
                        </span>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                      {draft.status}
                    </span>
                  </div>

                  {/* Editable Subject Line */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                      Subject Line (Editable)
                    </label>
                    <input
                      type="text"
                      value={displaySubject}
                      onChange={(e) => handleFieldChange(draft.id, 'suggestedSubject', e.target.value)}
                      className="w-full p-2.5 border rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  {/* Context Block */}
                  {draft.incomingBody && (
                    <div className="bg-gray-50 p-3 rounded-lg border">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Context Event / Incoming Email
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {decodeHtmlEntities(draft.incomingBody)}
                      </p>
                    </div>
                  )}

                  {/* Editable Draft Text Area */}
                  <div className="bg-blue-50/60 p-3 rounded-lg border border-blue-100 space-y-2">
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-semibold text-blue-600 uppercase">
                        TOM AI Response Draft (Editable)
                      </p>
                      {hasUnsavedEdits && (
                        <span className="text-[10px] font-bold text-amber-600 uppercase bg-amber-100 px-2 py-0.5 rounded border border-amber-200">
                          Unsaved Edits
                        </span>
                      )}
                    </div>

                    {isGenerating ? (
                      <div className="flex items-center gap-2 py-3 text-blue-600 font-semibold text-sm">
                        <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        <span>Generating response with Gemini 2.5 Flash...</span>
                      </div>
                    ) : (
                      <textarea
                        rows={24}
                        value={displayBody}
                        placeholder="Click 'Generate AI Draft' or type directly here to write a custom response..."
                        onChange={(e) => handleFieldChange(draft.id, 'suggestedBody', e.target.value)}
                        className="w-full p-3 border rounded-lg text-sm text-gray-900 leading-relaxed focus:ring-2 focus:ring-blue-500 outline-none bg-white min-h-[220px] resize-y"
                      />
                    )}
                  </div>

                  {/* Action Toolbar */}
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <button
                      onClick={() => handleSaveDraftEdits(draft)}
                      disabled={isGenerating || !hasUnsavedEdits}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition shadow-sm ${
                        hasUnsavedEdits && !isGenerating
                          ? "bg-amber-900 hover:bg-amber-600 text-white border-amber-600 cursor-pointer"
                          : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60"
                      }`}
                    >
                      Save Draft Edits
                    </button>
                    <button
                      onClick={() => handleRegenerate(draft)}
                      disabled={isGenerating}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white border hover:bg-gray-50 text-gray-700 font-medium rounded-lg shadow-sm disabled:opacity-50 transition"
                    >
                      {isGenerating ? (
                        <>
                          <svg className="animate-spin h-3.5 w-3.5 text-gray-600" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        draft.suggestedBody ? "Re-run AI Draft" : "Generate AI Draft"
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(draft)}
                      disabled={isGenerating}
                      className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
                    >
                      Delete Draft
                    </button>
                    <button
                      onClick={() => handleApproveAndSend(draft)}
                      disabled={isGenerating || (!displayBody && !draft.suggestedBody)}
                      className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm disabled:opacity-50"
                    >
                      Approve & Send
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="w-2/3 flex items-center justify-center text-gray-400">
          Select a customer from the sidebar to review pending drafts.
        </div>
      )}
    </div>
  );
}